import { Link } from "@tanstack/react-router";
import { type BillingInterval, intervalFor, type PlanId, unitPrice } from "@websede/contracts";
import { Check } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PublicLayout } from "../components/public-layout.js";
import { useFeatures } from "../features.js";

const PLAN_ORDER: PlanId[] = ["personal", "team"];
const FAQ = ["trial", "card", "cancel", "tax", "domain"] as const;

/** Public plans and prices, the same ones the Billing page sells (PLANS in contracts). */
export function PricingPage() {
  const { t, i18n } = useTranslation();
  const features = useFeatures();
  const [interval, setChosenInterval] = useState<BillingInterval>("month");
  const money = (minor: number) =>
    new Intl.NumberFormat(i18n.language, { style: "currency", currency: "USD" }).format(
      minor / 100,
    );
  return (
    <PublicLayout>
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-10 px-4 py-10">
        <section className="flex flex-col items-center gap-3 text-center">
          <h1 className="text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
            {t("public.pricing.title")}
          </h1>
          <p className="max-w-2xl text-base text-ink-2">{t("public.pricing.subtitle")}</p>
          <div className="mt-2 flex rounded-full border border-rule p-0.5 text-sm">
            {(["month", "year"] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={interval === value}
                onClick={() => setChosenInterval(value)}
                className={`rounded-full px-4 py-1.5 ${interval === value ? "bg-accent text-white" : "text-ink-2 hover:bg-ink/5"}`}
              >
                {value === "month" ? t("billing.monthly") : t("billing.yearly")}
              </button>
            ))}
          </div>
        </section>
        <section className="mx-auto grid w-full max-w-3xl gap-4 md:grid-cols-2">
          {PLAN_ORDER.map((id) => {
            const billed = intervalFor(id, interval);
            const price = unitPrice(id, billed);
            return (
              <article
                key={id}
                className={`flex flex-col gap-4 rounded-2xl border bg-surface p-6 ${id === "team" ? "border-accent" : "border-rule"}`}
              >
                <div>
                  <h2 className="text-lg font-medium text-ink">{t(`billing.plan.${id}.name`)}</h2>
                  <p className="text-sm text-ink-3">{t(`billing.plan.${id}.tagline`)}</p>
                </div>
                <p className="flex items-baseline gap-1">
                  <span className="text-4xl text-ink">{money(price)}</span>
                  <span className="text-sm text-ink-3">
                    {billed === "year" ? t("billing.perMailboxYear") : t("billing.perMailboxMonth")}
                  </span>
                </p>
                {billed !== interval ? (
                  <p className="-mt-3 text-xs text-ink-3">
                    {t("billing.yearlyOnly", { amount: money(Math.round(price / 12)) })}
                  </p>
                ) : null}
                <ul className="flex flex-1 flex-col gap-2 text-sm text-ink-2">
                  {(t(`billing.plan.${id}.features`, { returnObjects: true }) as string[]).map(
                    (feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <Check className="mt-0.5 size-4 shrink-0 text-good" />
                        {feature}
                      </li>
                    ),
                  )}
                </ul>
                <Link
                  to="/signup"
                  className={`inline-flex h-10 items-center justify-center rounded-full px-5 text-sm font-medium ${id === "team" ? "bg-accent text-white hover:shadow-1" : "border border-ink-3/40 text-accent hover:bg-accent-soft/60"} ${features.loaded ? "" : "invisible"}`}
                >
                  {features.signup ? t("public.nav.startTrial") : t("public.nav.comingSoon")}
                </Link>
              </article>
            );
          })}
        </section>
        <p className="text-center text-sm text-ink-3">{t("public.pricing.trialNote")}</p>
        <section className="mx-auto flex w-full max-w-3xl flex-col gap-4">
          <h2 className="text-xl font-medium text-ink">{t("public.pricing.faqTitle")}</h2>
          {FAQ.map((key) => (
            <details key={key} className="rounded-xl border border-rule bg-surface px-4 py-3">
              <summary className="cursor-pointer text-sm font-medium text-ink">
                {t(`public.pricing.faq.${key}.q`)}
              </summary>
              <p className="mt-2 text-sm text-ink-2">{t(`public.pricing.faq.${key}.a`)}</p>
            </details>
          ))}
        </section>
      </div>
    </PublicLayout>
  );
}
